import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartBar, faCalendarAlt, faBuilding, faSpinner,
  faFilePdf, faSearch, faCheckCircle, faExclamationTriangle
} from '@fortawesome/free-solid-svg-icons';
import { relatoriosService } from '../../services/relatoriosService';

const isoHoje = () => new Date().toISOString().slice(0, 10);
const isoAtras = (dias) => {
  const d = new Date();
  d.setDate(d.getDate() - dias);
  return d.toISOString().slice(0, 10);
};
const fmtData = (iso) => iso ? iso.split('-').reverse().join('/') : '';

const ExtensoesSemPedidos = () => {
  const [dataInicio, setDataInicio] = useState(isoAtras(30));
  const [dataFim, setDataFim] = useState(isoHoje());
  const [loading, setLoading] = useState(false);
  const [baixandoPdf, setBaixandoPdf] = useState(false);
  const [dados, setDados] = useState(null);
  const [erro, setErro] = useState(null);

  const consultar = async () => {
    setErro(null);
    if (!dataInicio || !dataFim) {
      setErro('Informe data inicial e final.');
      return;
    }
    if (dataInicio > dataFim) {
      setErro('A data inicial não pode ser maior que a data final.');
      return;
    }
    try {
      setLoading(true);
      const resp = await relatoriosService.extensoesSemPedidos({ dataInicio, dataFim });
      setDados(resp);
    } catch (e) {
      setErro(e.response?.data?.message || e.message || 'Erro ao consultar');
      setDados(null);
    } finally {
      setLoading(false);
    }
  };

  const baixarPdf = async () => {
    setErro(null);
    try {
      setBaixandoPdf(true);
      await relatoriosService.baixarExtensoesSemPedidosPdf({ dataInicio, dataFim });
    } catch (e) {
      setErro(e.message || 'Erro ao gerar PDF');
    } finally {
      setBaixandoPdf(false);
    }
  };

  // Ao mudar o período via preset, limpa a listagem atual para evitar
  // confusão entre o filtro novo e os dados do filtro anterior. O usuário
  // precisa clicar em "Consultar" de novo para ver o resultado.
  const aplicarPreset = (dias) => {
    setDataInicio(isoAtras(dias));
    setDataFim(isoHoje());
    setDados(null);
    setErro(null);
  };
  const aplicarPresetMes       = () => aplicarPreset(30);
  const aplicarPresetTrimestre = () => aplicarPreset(90);
  const aplicarPresetAno       = () => aplicarPreset(365);

  return (
    <div className="p-4 md:p-6 space-y-6">
      {/* Cabeçalho */}
      <div className="bg-white rounded-lg shadow p-5">
        <div className="flex items-center gap-2 mb-1">
          <FontAwesomeIcon icon={faChartBar} className="text-indigo-600" />
          <h1 className="text-xl font-bold text-gray-900">Extensões sem pedidos no período</h1>
        </div>
        <p className="text-sm text-gray-600">
          Listagem das extensões ativas que <strong>não tiveram nenhum pedido válido</strong> dentro do
          período selecionado. Considera apenas pedidos com status <strong>“Concluído”</strong> e
          <strong> valor maior que R$ 0,00</strong>.
        </p>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow p-5">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
          <div>
            <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">
              <FontAwesomeIcon icon={faCalendarAlt} className="text-indigo-600 mr-1" />
              Data inicial
            </label>
            <input
              type="date"
              value={dataInicio}
              onChange={(e) => setDataInicio(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 uppercase mb-1">
              <FontAwesomeIcon icon={faCalendarAlt} className="text-indigo-600 mr-1" />
              Data final
            </label>
            <input
              type="date"
              value={dataFim}
              onChange={(e) => setDataFim(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
          </div>
          <div className="md:col-span-2 flex flex-wrap gap-2">
            <button type="button" onClick={aplicarPresetMes}      className="px-3 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50">Últimos 30 dias</button>
            <button type="button" onClick={aplicarPresetTrimestre} className="px-3 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50">Últimos 90 dias</button>
            <button type="button" onClick={aplicarPresetAno}      className="px-3 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50">Último ano</button>
          </div>
        </div>
        <div className="mt-4 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={consultar}
            disabled={loading}
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded font-medium hover:bg-indigo-700 disabled:opacity-50"
          >
            <FontAwesomeIcon icon={loading ? faSpinner : faSearch} className={loading ? 'animate-spin' : ''} />
            {loading ? 'Consultando…' : 'Consultar'}
          </button>
          <button
            type="button"
            onClick={baixarPdf}
            disabled={baixandoPdf || !dados}
            className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded font-medium hover:bg-red-700 disabled:opacity-50"
            title={!dados ? 'Consulte primeiro para liberar a geração do PDF' : 'Gerar PDF para impressão'}
          >
            <FontAwesomeIcon icon={baixandoPdf ? faSpinner : faFilePdf} className={baixandoPdf ? 'animate-spin' : ''} />
            {baixandoPdf ? 'Gerando PDF…' : 'Gerar PDF'}
          </button>
        </div>

        {erro && (
          <div className="mt-3 bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">
            <FontAwesomeIcon icon={faExclamationTriangle} className="mr-2" />
            {erro}
          </div>
        )}
      </div>

      {/* Resultado */}
      {dados && (
        <div className="bg-white rounded-lg shadow">
          <div className="px-5 py-4 border-b flex flex-wrap items-center gap-3 justify-between">
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon={faBuilding} className="text-indigo-600" />
              <h2 className="font-semibold text-gray-900">
                Resultado: {dados.meta.total} extensão(ões) sem pedidos
              </h2>
            </div>
            <span className="text-xs text-gray-500">
              Período: {fmtData(dados.meta.dataInicio)} a {fmtData(dados.meta.dataFim)}
            </span>
          </div>

          {dados.data.length === 0 ? (
            <div className="p-10 text-center text-green-700">
              <FontAwesomeIcon icon={faCheckCircle} className="text-4xl mb-2" />
              <p className="font-medium">Todas as extensões ativas tiveram pedidos válidos no período.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className="bg-gray-50 text-gray-600 text-xs uppercase tracking-wide">
                  <tr>
                    <th className="px-4 py-3 text-left w-12">#</th>
                    <th className="px-4 py-3 text-left">Extensão</th>
                    <th className="px-4 py-3 text-left">Cidade</th>
                    <th className="px-4 py-3 text-left w-12">UF</th>
                    <th className="px-4 py-3 text-left">Pastor presidente</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {dados.data.map((e, i) => (
                    <tr key={e.id} className="hover:bg-gray-50">
                      <td className="px-4 py-2 text-gray-600">{i + 1}</td>
                      <td className="px-4 py-2 font-medium text-gray-900">{e.nome}</td>
                      <td className="px-4 py-2 text-gray-700">{e.cidade || '—'}</td>
                      <td className="px-4 py-2 text-gray-700">{e.uf || '—'}</td>
                      <td className="px-4 py-2 text-gray-700">{e.nomePastorPresidente || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ExtensoesSemPedidos;
